<template>
  <section class="view">
    <ViewLoader v-if="loading"/>
    <template v-else>

      <ViewHeader v-if="checkins.length">
        <template #title>{{ $t('pages.checkins_list.page_title') }}</template>
        <template #intro><span id="checkin_list_intro">{{ $t('pages.checkins_list.page_subtitle') }}</span></template>
      </ViewHeader>

      <div class="view-body view-body--padded checkin-list-body" v-if="checkins.length">
        <div class="container container-for-double-gutters">
          <div class="row row-double-gutters">
            <div class="col-xs-12 col-md-6 col-xl-4" v-for="checkin in checkins" :key="checkin.id">
              <CheckinsListItem :checkin="checkin" @item-clicked="openCheckinDetail($event)"
                                @anonymize-album="onAnonymize($event)"
                                @item-remove="openModalRemoveCheckin(checkin)"
                                @show-qr-picacode="openModalShowQrPicacode(checkin)"
                                @download-album="downloadAlbum(checkin)"/>
            </div>
          </div>

          <div style="height: 36px">
            <div ref="infiniteScroll" v-show="doPagination && !gettingCheckins">
              <view-loader :padding-classes="'py-2'"/>
            </div>
          </div>
        </div>
      </div>
      <FirstCheckinWelcome v-else/>
    </template>
    <ModalRemoveCheckin :checkin-id="selectedCheckin.id" :picacode="selectedCheckin.picacode" v-if="selectedCheckin"
                        @checkin-removed="removeCheckinFromList($event)"/>
    <ModalShowQrPicacode :picacode="selectedCheckin.picacode" v-if="selectedCheckin"/>

  </section>
</template>


<script>
import ViewLoader from "@/components/ViewLoader"
import ViewHeader from '@/components/ViewHeader'
import CheckinsListItem from '@/components/CheckinsListItem'
import FirstCheckinWelcome from "@/components/FirstCheckinWelcome"
import ModalRemoveCheckin from "@/components/ModalRemoveCheckin"
import VoucherService from "@/services/Voucher"
import EventService from "@/services/Event";
import {mapActions, mapGetters, mapMutations} from "vuex";
import ModalShowQrPicacode from "@/components/ModalShowQrPicacode.vue";
import {IntersectionObserverHelper} from "@/domain/core/helper/IntersectionObserverHelper";

export default {
  components: {
    ViewLoader,
    ViewHeader,
    CheckinsListItem,
    FirstCheckinWelcome,
    ModalRemoveCheckin,
    ModalShowQrPicacode,
  },

  data() {
    return {
      loading: true,
      selectedCheckin: null,
      observer: null,
      gettingCheckins: false,
    }
  },

  mounted() {
    this.CLEAR_CHECKINS()
  },

  beforeCreate() {
    const voucher = this.$store.getters['getVoucherToReedem']
    this.$store.commit('SET_VOUCHER_TO_REEDEM', '')
    // Redeem multi-event voucher
    if (voucher) {
      VoucherService.redeemVoucher(voucher)
          .then(() => {
            setTimeout(() => this.$bvToast.toast(this.$t('modal_voucher.valid_voucher_feedback'),
                    {
                      autoHideDelay: 200000,
                      headerClass: '',
                      noCloseButton: false
                    }),
                200)
          })
    }
  },

  watch: {
    getAppConfigReady: {
      handler: function (value) {
        if (value) {
          this.loadCheckinListData()
        }
      },
      immediate: true
    }
  },

  computed: {
    ...mapGetters('appConfig', [
      'getAppConfigReady', 'hideCart'
    ]),
    ...mapGetters('digitalItems', [
      'checkins', 'pagination', 'doPagination'
    ])
  },

  methods: {
    ...mapActions('checkin', ['openAddSelfieStep']),
    ...mapMutations('digitalItems', ['SET_DO_PAGINATION', 'INCREMENT_PAGINATION', 'CLEAR_CHECKINS']),
    ...mapActions('digitalItems', { fetchCheckins: 'getCheckins' }),
    ...mapActions('cartDigitalItems', ['fetchDigitalItemsTotals']),

    async loadCheckinListData() {
      this.CLEAR_CHECKINS()
      if (!this.hideCart) {
        await this.fetchDigitalItemsTotals()
        await this.getCheckins()
      } else {
        await this.getCheckins()
      }
      this.subscribeToWebSocketCheckinEvents()
    },
    async getCheckins() {
      this.gettingCheckins = true
      this.fetchCheckins(this.pagination).then(async (response) => {
        if (response.length === 0) {
          this.SET_DO_PAGINATION(false)
          this.loading = false
          return
        }
        this.loading = false
        this.INCREMENT_PAGINATION()

        await this.$nextTick()
        if (!this.$refs.infiniteScroll) {
          this.disconnectObserver()
          return
        }
        this.observer = new IntersectionObserverHelper(this.$refs.infiniteScroll, {
          threshold: 1,
        }, this.observeCallback.bind(this))
        await this.$nextTick()
        this.gettingCheckins = false
      }).catch(() => {
        this.loading = false
        this.gettingCheckins = false
      })
    },
    observeCallback(entry) {
      if (entry.intersectionRatio >= 1 && !this.gettingCheckins && this.doPagination) {
        if (!this.timeoutSet) {
          this.timeoutSet = true;
          setTimeout(() => {
            this.timeoutSet = false;
            this.getCheckins();
          }, 700);
        }
      }
    },
    openCheckinDetail(checkinData) {
      this.$router.push({
        name: 'checkins-detail',
        params: {picacode: checkinData.picacode, openFaceReconition: checkinData.openFaceReconition}
      })
    },
    subscribeToWebSocketCheckinEvents() {
      // Subscribe to checkin events
      this.$store.dispatch('websocket/sendMessage',
          {'type': 'checkin.subscribe', 'data': {'picacodes': this.checkins.map(i => i.picacode)}}
      )
    },
    openModalRemoveCheckin(checkin) {
      this.selectedCheckin = checkin
      // we use $nextTick to make sure that the component exists when we show the modal
      this.$nextTick(() => this.$bvModal.show('bv-modal-remove-checkin'))
    },
    openModalShowQrPicacode(checkin) {
      this.selectedCheckin = checkin
      this.$nextTick(() => this.$bvModal.show('bv-modal-show-qr-picacode'))
    },
    removeCheckinFromList(checkinId) {
      this.$store.commit('digitalItems/REMOVE_CHECKIN', checkinId)
    },
    downloadAlbum(checkin) {
      EventService.downloadAlbum(checkin.id)
          .then(response => {
            this.$bvToast.toast(
                this.$t('toast_download_album.text'),
                {
                  title: this.$t('toast_download_album.title'),
                  headerClass: 'd-block pt-2',
                  noCloseButton: false,
                  autoHideDelay: 5000
                })
          })
          .catch(error => {
            if (error.response.status === 400 && error.response.data.code === 'bulk_download_not_available') {
              this.$bvToast.toast(
                  error.response.data.detail,
                  {variant: 'warning', autoHideDelay: 5000})
            }
          })
    },
    onAnonymize({picacode}) {
      this.$router.push({
        name: 'checkins-detail',
        params: {picacode: picacode, openAnonymize: true}
      })
    },
    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect()
      }
    }
  },
  beforeDestroy() {
    this.CLEAR_CHECKINS()
    this.disconnectObserver()
  }
}
</script>
